/* eslint-disable-next-line max-lines */
import type { Identifier, XYCoord } from 'dnd-core';
import { FC, useState } from 'react';
import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { CardProps } from 'reactstrap';
import useCheckMobileScreen from '../../../hooks/useCheckMobileScreen';
import { deleteItemAlert } from './DeleteFileAlert';
import { cardHoverHeight, DragItem, HoverDivInfo, hoverPanel, redirectToPdf } from './FileCardUtils';
import { DocumentsStateManager } from './DocumentsStateManager';
import { isAdmin } from '../../../helpers';

const ItemTypes = {
  CARD: 'card',
};

const style = {
  marginBottom: '.5rem',
  cursor: 'move',
  display: 'inline-grid',
  paddingLeft: '0px',
  paddingRight: '0px',
  minWidth: '240px',
  minHeight: '240px',
};

export type FileCardProps = CardProps & {
  documentsState: DocumentsStateManager;
};

export const DragDropFilesCard: FC<FileCardProps> = ({ card, moveCard, documentsState, index }) => {
  const { isMobile } = useCheckMobileScreen();
  const ref = useRef<HTMLDivElement>(null);
  const [_, setIsLoaded] = useState(false);
  const [hoverDiv, setHoverDiv] = useState<HoverDivInfo>({
    visible: false,
    x: 0,
    y: 0,
    width: 300,
    height: 100,
  });

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: ItemTypes.CARD,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      const idFolder = item.id;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveCard(dragIndex, hoverIndex, idFolder);

      // Note: we're mutating the monitor item here!
      // Generally, it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.CARD,
    item: () => {
      return { card, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  return (
    <>
      {documentsState.hasDocumentShowPermission() && (
        <div
          className={isMobile ? 'col-6' : 'col-1'}
          ref={ref}
          style={{ ...style, opacity }}
          data-handler-id={handlerId}
          id={`folderPosition-${index}`}
          tabIndex={documentsState.state.hoverIndex === index ? -1 : undefined}
        >
          <div
            style={{
              position: 'relative',
              zIndex: '99999999',
              minWidth: '240px',
              maxWidth: '240px',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {hoverDiv?.visible && isAdmin(documentsState.state.user) && hoverPanel(card, hoverDiv)}
            <div
              style={{}}
              className="text-center"
              onMouseEnter={() => {
                documentsState.setHoverIndex(index);
                setHoverDiv({
                  ...hoverDiv,
                  visible: true,
                });
              }}
              onMouseMove={(e) => {
                setHoverDiv({
                  ...hoverDiv,
                  x: e.nativeEvent.offsetX,
                  y: e.nativeEvent.offsetY,
                  height: cardHoverHeight(card),
                });
              }}
              onMouseLeave={() => {
                setHoverDiv({
                  ...hoverDiv,
                  visible: false,
                });
              }}
              onClick={async () => {
                await redirectToPdf(card.id, documentsState);
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundSize: 'cover',
                  backgroundImage: `url("data:image/jpeg;base64,${card.pictureBlobBase64}")`,
                  minWidth: '200px',
                  minHeight: '220px',
                  maxWidth: '200px',
                  maxHeight: '220px',
                }}
                onLoad={() => {
                  setIsLoaded(true);
                }}
              />
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}
              >
                <div style={{ textAlign: 'center' }}>{card.name}</div>
                {!isMobile && (
                  <div style={{ textAlign: 'center', paddingBottom: '10px' }}>
                    <div className="btn-group btn-group-sm" role="group" aria-label="Basic example">
                      {documentsState.hasDocumentEditPermission() && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            documentsState.modalFilesShow(card);
                          }}
                        >
                          <i className="far fa-edit text-blue"></i>
                        </button>
                      )}
                      {documentsState.hasDocumentDeletePermission() && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ cursor: 'pointer' }}
                          onClick={async () => {
                            await deleteItemAlert(card, documentsState);
                          }}
                        >
                          <i className="far fa-trash-alt text-danger"></i>
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
